import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import type React from "react";
import { useEffect, useState } from "react";
import { blockUser } from "../../../apis/blocking/blocking-apis";
import {
  BLOCK_REASONS,
  type BlockReason,
  type BlockUserRequest,
} from "../../../domain/blocking";
import { useShowToast } from "../../../hooks";
import { strings } from "../../../I18n";

interface BlockUserModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
  onSuccess: () => void;
}

const BlockUserModal: React.FC<BlockUserModalProps> = ({
  open,
  onClose,
  userId,
  userName,
  onSuccess,
}) => {
  const [reason, setReason] = useState<BlockReason | "">("");
  const [message, setMessage] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const showToast = useShowToast();

  useEffect(() => {
    if (open) {
      setReason("");
      setMessage("");
      setInternalNotes("");
    }
  }, [open]);

  useEffect(() => {
    if (reason && reason !== "OTHER") {
      const reasonOption = BLOCK_REASONS.find((r) => r.value === reason);
      if (reasonOption?.defaultMessage) {
        setMessage(reasonOption.defaultMessage);
      }
    }
  }, [reason]);

  const handleBlock = async () => {
    if (!reason) return;
    if (reason === "OTHER" && !message.trim()) {
      showToast(strings("blocking.messageRequiredForOther"), "error");
      return;
    }

    setLoading(true);
    try {
      const request: BlockUserRequest = {
        reason,
        message: message.trim(),
        internalNotes: internalNotes.trim() || undefined,
      };
      await blockUser(userId, request);
      showToast(strings("blocking.userBlocked"), "success");
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast(
        error.response?.data?.message || strings("blocking.blockFailed"),
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningAmberIcon color="warning" />
        {strings("blocking.blockUser")}
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3, mt: 1 }}>
          {strings("blocking.blockWarning", { userName })}
        </Alert>

        <FormControl fullWidth={true} sx={{ mb: 2 }}>
          <InputLabel>{strings("blocking.reason")}</InputLabel>
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value as BlockReason)}
            label={strings("blocking.reason")}
          >
            {BLOCK_REASONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label={strings("blocking.userVisibleMessage")}
          placeholder={strings("blocking.userVisibleMessagePlaceholder")}
          fullWidth={true}
          multiline={true}
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{ mb: 2 }}
          helperText={strings("blocking.userVisibleMessageHelper")}
          required={reason === "OTHER"}
          error={reason === "OTHER" && !message.trim()}
        />

        <TextField
          label={strings("blocking.internalNotes")}
          placeholder={strings("blocking.internalNotesPlaceholder")}
          fullWidth={true}
          multiline={true}
          rows={2}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          helperText={strings("blocking.internalNotesHelper")}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          {strings("buttons.cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleBlock}
          disabled={!reason || loading}
          startIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <WarningAmberIcon />
            )
          }
        >
          {loading
            ? strings("blocking.blocking")
            : strings("blocking.blockUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlockUserModal;
